import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Users as UsersIcon, Search, FileDown, ArrowUpDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmt2 = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

const COLS = [
  { key: 'rank', label: '#', num: true },
  { key: 'name', label: 'الموظف' },
  { key: 'offices', label: 'المكتب' },
  { key: 'income', label: 'الإيراد', num: true },
  { key: 'transactions', label: 'المعاملات', num: true },
  { key: 'items', label: 'العدد', num: true },
  { key: 'weight', label: 'الوزن', num: true },
  { key: 'active_days', label: 'أيام النشاط', num: true },
  { key: 'avg_per_txn', label: 'متوسط/معاملة', num: true },
  { key: 'avg_per_day', label: 'متوسط/يوم', num: true },
  { key: 'share_pct', label: 'الحصة %', num: true },
  { key: 'top_service', label: 'أعلى خدمة' },
];

const cell = (r, k) => {
  if (k === 'offices') return (r.offices || []).join('، ') || '—';
  if (k === 'share_pct') return `${fmt2(r.share_pct)}%`;
  if (k === 'avg_per_txn' || k === 'avg_per_day') return fmt2(r[k]);
  if (['income', 'transactions', 'items', 'weight'].includes(k)) return fmt(r[k]);
  return r[k] ?? '—';
};

export const TabloEmployeeKpis = ({ filterParams, onExport }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'income', dir: -1 });

  useEffect(() => {
    setRows(null);
    axios.get(`${API_URL}/api/super/tablo/tablo/employees-kpi`, { params: filterParams })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]));
  }, [filterParams]);

  const view = useMemo(() => {
    const t = q.trim();
    const list = (rows || []).filter((r) => !t || r.name.includes(t) || (r.offices || []).join(' ').includes(t));
    return list.slice().sort((a, b) => {
      const va = a[sort.key]; const vb = b[sort.key];
      if (typeof va === 'number') return (va - vb) * sort.dir;
      return String(va || '').localeCompare(String(vb || ''), 'ar') * sort.dir;
    });
  }, [rows, q, sort]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: ['rank', 'name', 'offices', 'top_service'].includes(key) ? 1 : -1 }));

  return (
    <Card className="overflow-hidden" data-testid="tablo-employee-kpis">
      <div className="px-4 py-3 border-b border-border bg-accent/20 flex flex-wrap items-center gap-3">
        <h3 className="font-bold text-sm flex items-center gap-2 flex-1 min-w-[200px]">
          <UsersIcon className="h-4 w-4 text-primary" />
          مؤشرات أداء الموظفين حسب الفلاتر ({rows ? view.length : '…'})
        </h3>
        <div className="relative w-56">
          <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث موظف أو مكتب..." className="h-8 pr-7 text-xs" data-testid="tablo-emp-kpi-search" />
        </div>
        <button type="button" onClick={onExport} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors" data-testid="tablo-emp-kpi-export">
          <FileDown className="h-3.5 w-3.5" /> تصدير Excel
        </button>
      </div>
      <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
        <table className="w-full text-xs" data-testid="tablo-emp-kpi-table">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              {COLS.map((c) => (
                <th key={c.key} className={`px-3 py-2 font-bold whitespace-nowrap ${c.num ? 'text-left' : 'text-right'}`}>
                  <button type="button" onClick={() => toggleSort(c.key)} className={`inline-flex items-center gap-1 hover:text-primary ${sort.key === c.key ? 'text-primary' : ''}`} data-testid={`tablo-emp-kpi-sort-${c.key}`}>
                    {c.label}<ArrowUpDown className="h-3 w-3 opacity-50" />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows === null ? (
              <tr><td colSpan={COLS.length} className="py-8 text-center text-foreground/50">جاري التحميل...</td></tr>
            ) : view.length === 0 ? (
              <tr><td colSpan={COLS.length} className="py-8 text-center text-foreground/50">لا توجد نتائج</td></tr>
            ) : view.map((r) => (
              <tr key={r.name} className="hover:bg-accent/20" data-testid={`tablo-emp-kpi-row-${r.rank}`}>
                {COLS.map((c) => (
                  <td key={c.key} className={`px-3 py-2 ${c.num ? 'text-left font-numbers' : ''} ${c.key === 'income' ? 'font-bold text-emerald-700' : ''} ${c.key === 'name' ? 'font-bold whitespace-nowrap' : ''}`}>
                    {cell(r, c.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
